import {
  ChangeDetectionStrategy,
  Component,
  computed,
  DestroyRef,
  inject,
  input,
  linkedSignal,
  OnInit,
  signal,
} from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { MatButton } from '@angular/material/button';
import { MatFormField, MatLabel } from '@angular/material/form-field';
import { MatIcon } from '@angular/material/icon';
import { MatInput } from '@angular/material/input';
import { MatProgressSpinner } from '@angular/material/progress-spinner';
import { MatSlideToggle } from '@angular/material/slide-toggle';
import { TranslatePipe } from '@ngx-translate/core';
import type { ModuleRunParameter, ModuleRunParameters } from '../../models/test-module.model';
import { TestModuleService } from '../../services/test-module.service';
import { FIELD_LENGTHS } from '../../shared/field-lengths';

export type RunParametersMode = 'MANUAL' | 'AUTOMATED';

type LoadState = 'loading' | 'failed' | 'loaded';

const INPUT_TYPES: Readonly<Partial<Record<ModuleRunParameter['type'], string>>> = {
  SECRET: 'password',
  NUMBER: 'number',
  DATE: 'date',
};

@Component({
  selector: 'app-run-parameters-section',
  imports: [MatButton, MatFormField, MatIcon, MatInput, MatLabel, MatProgressSpinner, MatSlideToggle, TranslatePipe],
  templateUrl: './run-parameters-section.component.html',
  styleUrl: './run-parameters-section.component.scss',
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class RunParametersSectionComponent implements OnInit {
  private static nextId = 0;

  private readonly testModules = inject(TestModuleService);
  private readonly destroyRef = inject(DestroyRef);

  readonly projectId = input.required<string>();
  readonly moduleId = input.required<string>();
  readonly mode = input.required<RunParametersMode>();

  readonly fieldLengths = FIELD_LENGTHS;
  private readonly idPrefix = `run-param-${RunParametersSectionComponent.nextId++}`;
  readonly labelId = `${this.idPrefix}-label`;

  readonly loadState = signal<LoadState>('loading');
  private readonly runParameters = signal<ModuleRunParameters | null>(null);

  private readonly missing = computed(() => this.runParameters()?.parameters.filter((p) => p.missing) ?? []);

  readonly fields = computed(() => this.missing().filter((p) => this.mode() === 'MANUAL' || !p.secret));

  readonly missingSecrets = computed(() =>
    this.mode() === 'AUTOMATED'
      ? this.missing()
          .filter((p) => p.secret)
          .map((p) => p.name)
      : [],
  );

  /** Beim Wechsel zu „Automatisiert“ fallen eingegebene Secrets weg, die uebrigen Werte bleiben. */
  private readonly entered = linkedSignal<RunParametersMode, Record<string, string>>({
    source: this.mode,
    computation: (mode, previous) => {
      const current = previous?.value ?? {};
      return mode === 'AUTOMATED' ? this.withoutSecrets(current) : current;
    },
  });

  /** Ein Schalter hat immer einen Wert; unberuehrt ist er „false“. */
  readonly values = computed(() => {
    const entered = this.entered();
    const values: Record<string, string> = {};
    for (const parameter of this.fields()) {
      const value = entered[parameter.name] ?? (parameter.type === 'BOOLEAN' ? 'false' : '');
      if (value.trim()) {
        values[parameter.name] = value;
      }
    }
    return values;
  });

  readonly ready = computed(
    () =>
      this.loadState() === 'loaded' &&
      this.missingSecrets().length === 0 &&
      this.fields().every((parameter) => parameter.name in this.values()),
  );

  readonly sourceKind = computed(() => this.runParameters()?.source.kind ?? null);

  readonly sourceName = computed(() => {
    const source = this.runParameters()?.source;
    return source?.dataSheetKey ?? source?.dataSheetName ?? '';
  });

  ngOnInit(): void {
    this.load();
  }

  load(): void {
    this.loadState.set('loading');
    this.testModules
      .getModuleRunParameters(this.projectId(), this.moduleId())
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe({
        next: (runParameters) => {
          this.runParameters.set(runParameters);
          this.loadState.set('loaded');
        },
        error: () => this.loadState.set('failed'),
      });
  }

  inputType(parameter: ModuleRunParameter): string {
    return INPUT_TYPES[parameter.type] ?? 'text';
  }

  valueOf(name: string): string {
    return this.entered()[name] ?? '';
  }

  fieldId(index: number): string {
    return `${this.idPrefix}-${index}`;
  }

  set(name: string, value: string): void {
    this.entered.update((entered) => ({ ...entered, [name]: value }));
  }

  private withoutSecrets(values: Record<string, string>): Record<string, string> {
    const secrets = new Set(
      this.missing()
        .filter((p) => p.secret)
        .map((p) => p.name),
    );
    return Object.fromEntries(Object.entries(values).filter(([name]) => !secrets.has(name)));
  }
}
